import { TableName } from "../../../../tables"
import { SalePaymentItemData } from "./types"
import { Data2 } from "../../../../features/data-types"
import { useSaleOperationLoaders } from "../../loaders/saleOperationLoaders"
import { useSaleOperationBuilders } from "../../builders/saleOperationBuilders"
import { PaymentCategory, usePaymentTypeLoaders, usePaymentTypeHooks, useAdministrationService, useAdjustmentNoteService, useTransactionService, TransactionSourceType, TransactionDestinationType, ManualCreditNoteRequest } from "../../../administration"
import { Item, PaymentStatus, useOperationBuilders, usePriceOutlineHooks, usePricesHooks } from "../../../operations"
import { useStockService } from "../../../stock"
import { firstItem, toList } from "../../../../utils/listUtils"
import { useSaleOperationActions } from "../../actions/saleOperationActions"
import { fileHelpers, useFileActions } from "../../../../features/files"
import { O } from "../../../../features/ui"
import { Customer, SaleOperation, SalePaymentItem } from "../../state/types"
import { isDefined, round } from "../../../../utils/typeUtils"
import { useCustomerLoaders } from "../../loaders/customerLoaders"
import { useCustomerBuilders } from "../../builders/customerBuilders"
import { useCustomerActions } from "../../actions/customerActions"
import { useSaleOperationHooks } from "../../hooks/saleOperationHooks"
import { useServer } from "../../../../services/server"
import { InvoiceItemQuantity, InvoiceMode } from "../../state/types"

export const useSaleOperationService = () => {
    const { paymentReceiptFilePath, isPayable } = useSaleOperationHooks()
    const pricesHooks = usePricesHooks()
    const { getAdjustedTotal } = usePriceOutlineHooks()
    const { getPaymentTypeYield, isCreditAccount } = usePaymentTypeHooks()

    const { buildSaleOperation, buildSalePaymentItem } = useSaleOperationBuilders()
    const { buildItem } = useOperationBuilders()
    const { buildCustomer } = useCustomerBuilders()

    const saleOperationLoaders = useSaleOperationLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const customerLoaders = useCustomerLoaders()

    const { accreditItemPayment, confirmItemPayment, createItemCheck } = useAdministrationService()
    const { createManualCreditNote: createManualAdjustmentNote, createOverpaymentNote } = useAdjustmentNoteService()
    const { createTransaction } = useTransactionService()
    const { saveSaleOperation } = useSaleOperationActions()
    const { reserveStock, enableStock } = useStockService()
    const { saveCustomer } = useCustomerActions()
    const { saveFile } = useFileActions()
    const { emitFiscalDocument } = useServer()

    const buildSaleOperationItems = (
        itemsData: Data2[],
        customerTypeId?: string
    ) => {
        const items = itemsData.map(itemData => {
            const customerTypeYield = customerTypeId ? pricesHooks.product.customerTypeYield(customerTypeId, itemData.productId! as string) : itemData.customerTypeYield
            const itemVatTaxYield = itemData.vatTaxYield as O<number>
            const vatTaxYield = isDefined(itemVatTaxYield) ? itemVatTaxYield : pricesHooks.product.vatTaxYield(itemData.productId! as string)
            return buildItem({
                ...itemData,
                customerTypeYield,
                vatTaxYield
            })
        })
        return items
    }

    // Used when creating an operation (budget items carry their own delivery stats) and when editing it
    // (only allowed before emitting deliveries): delivery stats start over from the new quantities
    const buildEditedSaleOperationItems = (
        itemsData: Data2[],
        customerTypeId?: string
    ) => {
        const itemsDataWithoutStats = itemsData.map(({ deliveryStats, ...itemData }) => itemData)
        return buildSaleOperationItems(itemsDataWithoutStats, customerTypeId)
    }

    // Reserves or releases the stock difference between the previous and the new items.
    // Runs sequentially because stock updates read and write the same record.
    const updateReservedStock = async (
        previousItems: Item[],
        previousBranchId: string,
        newItems: Item[],
        newBranchId: string
    ) => {
        const stockKey = (branchId: string, item: Item) => `${branchId}|${item.productId}|${item.lotId || ''}`
        const diffs = new Map<string, { branchId: string, productId: string, lotId?: string, quantity: number }>()
        const addDiff = (branchId: string, item: Item, quantity: number) => {
            const key = stockKey(branchId, item)
            const diff = diffs.get(key) || { branchId, productId: item.productId, lotId: item.lotId, quantity: 0 }
            diffs.set(key, { ...diff, quantity: diff.quantity + quantity })
        }
        previousItems.forEach(item => addDiff(previousBranchId, item, -(item.quantity - (item.cancelledQuantity || 0))))
        newItems.forEach(item => addDiff(newBranchId, item, item.quantity - (item.cancelledQuantity || 0)))

        for (const { branchId, productId, lotId, quantity } of Array.from(diffs.values())) {
            if (quantity > 0) {
                await reserveStock(productId, branchId, quantity, lotId)
            } else if (quantity < 0) {
                await enableStock(productId, branchId, -quantity, lotId)
            }
        }
    }

    // Registers an adjustment note amount on the operation (credit notes positive, debit notes negative).
    // The operation total never changes: what the customer owes is the total minus the adjusted amount.
    // If the operation was already paid above that, the excess goes to the customer credit in favor
    // and the operation paid amount is lowered by it, so the same amount can't be credited twice.
    const applyAdjustmentNote = async (
        table: TableName,
        operationId: string,
        noteAmount: number
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        const adjustedAmount = round((loadedOperation.adjustedAmount || 0) + noteAmount)
        const adjustedTotal = getAdjustedTotal(table, { ...loadedOperation, adjustedAmount })
        const paidAmount = round(loadedOperation.paidAmount || 0)

        const excess = Math.max(0, round(paidAmount - adjustedTotal))
        const newPaidAmount = round(paidAmount - excess)
        const paymentStatus = paidAmount === 0 ? loadedOperation.paymentStatus :
            newPaidAmount >= adjustedTotal ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED
        await updateSaleOperationProps(table, operationId, { adjustedAmount, paidAmount: newPaidAmount, paymentStatus })

        if (excess > 0) {
            const { loadCustomer } = customerLoaders()
            const loadedCustomer = (await loadCustomer(loadedOperation.customerId, true))!
            const updatedCustomer = buildCustomer({
                ...loadedCustomer,
                creditInFavor: round((loadedCustomer.creditInFavor || 0) + excess)
            })
            await saveCustomer(updatedCustomer)
        }

        return excess
    }

    // Manual credit note (e.g. a missing discount or a wrong price): prorated over the items or on the corrected products.
    // The server splits it in a fiscal note (what is already invoiced) and an internal one, and saves it with the
    // operation in a single transaction: the amount is applied once, each item keeps its discount per unit (a later
    // return or cancellation credits net of it) and, if the operation was already paid, the excess goes to the customer
    // credit in favor. The operation is read from the db so the totals sent are the current ones
    const createManualCreditNote = async (
        table: TableName,
        operationId: string,
        request: ManualCreditNoteRequest,
        reason: string
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        const { adjustmentNotes } = await createManualAdjustmentNote(table, loadedOperation, request, reason)
        return adjustmentNotes
    }

    const updateSaleOperationProps = async (
        table: TableName,
        operationId: string,
        props: Partial<SaleOperation>,
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = await loadSaleOperation(table, operationId, true)

        const operation = buildSaleOperation(table, {
            ...loadedOperation!,
            ...props
        })

        return saveSaleOperation(table, operation)
    }

    // Rounding is only allowed when every payment type allows it, up to the most restrictive max and the pending amount
    const validateRounding = (salePaymentItems: SalePaymentItemData[], pendingAmount: number) => {
        const roundingAmount = salePaymentItems.filter(item => item.isRounding).reduce((sum, item) => sum + item.amount, 0)
        if (roundingAmount === 0) return

        const { findPaymentType } = paymentTypeLoaders()
        const paymentTypes = salePaymentItems.filter(item => !item.isRounding).map(item => findPaymentType(item.paymentTypeId))
        const allAllowRounding = paymentTypes.length > 0 && paymentTypes.every(paymentType => paymentType?.roundingEnabled && (paymentType.maxRoundingAmount || 0) > 0)
        if (!allAllowRounding) {
            throw new Error('Rounding is not allowed for the selected payment types')
        }

        const maxRoundingAmount = Math.min(...paymentTypes.map(paymentType => paymentType!.maxRoundingAmount!))
        const paidWithoutRounding = salePaymentItems.filter(item => !item.isRounding).reduce((sum, item) => sum + item.amount, 0)
        const pendingWithoutRounding = round(pendingAmount - paidWithoutRounding)
        if (roundingAmount < 0 || round(roundingAmount) > round(Math.min(maxRoundingAmount, pendingWithoutRounding))) {
            throw new Error('Rounding amount exceeds the allowed max')
        }
    }

    const confirmPayment = async (
        table: TableName,
        operation: SaleOperation,
        customer: Customer,
        paymentDate: Date,
        salePaymentItems: SalePaymentItemData[],
        amountToPay?: number
    ) => {
        // A negative credit in favor (left by a previous partial payment bug) isn't a debt: it's ignored and reset to 0 below
        const creditInFavor = Math.max(0, customer.creditInFavor || 0)
        const sortedPaymentItems = salePaymentItems.sort((a, b) => {
            if (a.isRounding) return 1
            if (b.isRounding) return -1
            return a.paymentTypeId!.localeCompare(b.paymentTypeId!)
        })
        // A credit account operation keeps its payment type: an amount collected now (e.g. a hoard extension) only lowers its debt.
        // A payment covered 100% with credit in favor has no payment medium: the operation keeps no paymentTypeId.
        // The pricing condition isn't lost: the operation stores its paymentTypeYield and uses it when there is no id.
        const isCreditAccountOperation = isCreditAccount(operation.paymentTypeId)
        const newPaymentTypeId = isCreditAccountOperation ? operation.paymentTypeId : firstItem(sortedPaymentItems)?.paymentTypeId
        const paymentTypeChanged = newPaymentTypeId && operation!.paymentTypeId !== newPaymentTypeId
        const outlinePaymentTypeId = paymentTypeChanged ? newPaymentTypeId : undefined
        const adjustedTotal = getAdjustedTotal(table, operation!, outlinePaymentTypeId)

        // What was already paid is discounted, so an operation can be paid in parts (e.g. a financial hoard whose value was extended).
        // amountToPay limits the payment to part of what is owed (e.g. only the extension of a credit account hoard)
        const pendingAmount = round(adjustedTotal - (operation.paidAmount || 0))
        const amountDue = amountToPay !== undefined ? Math.min(round(amountToPay), pendingAmount) : pendingAmount

        validateRounding(sortedPaymentItems, round(amountDue - creditInFavor))

        let receiptIndex = 0
        let unpaidAmount = amountDue
        let paidAmount = 0
        // Received money above what was owed (the credit in favor used isn't new money, so it doesn't count)
        let overpaidAmount = 0
        const paymentItems: SalePaymentItem[] = []

        if (creditInFavor > 0) {
            paidAmount += creditInFavor
            const itemAmount = Math.min(creditInFavor, unpaidAmount)
            unpaidAmount = round(unpaidAmount - itemAmount)

            const paymentItem = buildSalePaymentItem({
                creditInFavor: true,
                amount: itemAmount,
                paymentDate
            })
            paymentItems.push(paymentItem)
        }

        for (const salePaymentItem of sortedPaymentItems) {
            const { paymentTypeId, cashFundId, amount, receiptFile, isRounding } = salePaymentItem

            paidAmount += amount
            const itemAmount = Math.min(amount, unpaidAmount)
            unpaidAmount = round(unpaidAmount - itemAmount)

            if (isRounding) {
                const sourceType = table === TableName.SALE_ORDERS ? TransactionSourceType.SALE : TransactionSourceType.HOARD
                await createTransaction({
                    sourceType,
                    sourceId: operation.id,
                    destinationType: TransactionDestinationType.ROUNDING,
                    amount: itemAmount
                })
                const paymentItem = buildSalePaymentItem({
                    isRounding: true,
                    amount: itemAmount,
                    paymentDate
                })
                paymentItems.push(paymentItem)
                continue
            }

            overpaidAmount = round(overpaidAmount + amount - itemAmount)

            const { findPaymentType } = paymentTypeLoaders()
            const paymentType = findPaymentType(paymentTypeId)!

            let destinationId: string
            if (paymentType.category === PaymentCategory.CHECK) {
                const savedCheck = await createItemCheck(salePaymentItem, paymentDate)
                destinationId = savedCheck.id
                await accreditItemPayment(table, operation.id, paymentTypeId!, destinationId, amount, paymentDate)
            } else {
                await confirmItemPayment(table, operation.id, salePaymentItem, paymentDate)
                destinationId = cashFundId!
            }

            let receiptUrl: O<string>
            if (receiptFile) {
                receiptIndex++
                const ext = fileHelpers.getFileExtension(receiptFile)
                const filePath = paymentReceiptFilePath(table, operation!, receiptIndex, ext)
                receiptUrl = await saveFile(filePath, receiptFile)
            }

            const paymentItem = buildSalePaymentItem({
                paymentTypeId,
                destinationId,
                amount: itemAmount,
                paymentDate,
                receiptUrl
            })
            paymentItems.push(paymentItem)
        }

        // paidAmount includes the whole credit in favor, so what is left of it is what was paid above the pending amount
        const newCreditInFavor = Math.max(0, round(paidAmount - amountDue))
        if (customer.creditInFavor !== newCreditInFavor) {
            const updatedCustomer = buildCustomer({
                ...customer,
                creditInFavor: newCreditInFavor
            })
            await saveCustomer(updatedCustomer)
        }

        const newPaymentTypeYield = paymentTypeChanged ? getPaymentTypeYield(newPaymentTypeId) : operation!.paymentTypeYield
        const loadedPaymentItems = operation!.paymentItems
        const newPaymentItems = [...loadedPaymentItems, ...paymentItems]
        // Added to the previous paid amount (not recalculated from the payment items): a credit account operation also
        // has credit payments imputed to it, and adjustment notes can lower the paid amount without touching the items
        const newPaidAmount = round((operation.paidAmount || 0) + paymentItems.reduce((sum, item) => sum + item.amount, 0))
        const receivedAmount = round(newPaymentItems.filter(paymentItem => !paymentItem.creditInFavor && !paymentItem.isRounding).reduce((sum, item) => sum + item.amount, 0))
        const initialCredit = (operation.initialCredit || 0) + creditInFavor
        const isFullyPaid = newPaidAmount >= adjustedTotal
        const paidOperation = buildSaleOperation(table, {
            ...operation,
            paymentTypeId: newPaymentTypeId,
            paymentTypeYield: newPaymentTypeYield,
            paymentItems: newPaymentItems,
            paidAmount: newPaidAmount,
            receivedAmount,
            overpaidAmount: overpaidAmount > 0 ? round((operation.overpaidAmount || 0) + overpaidAmount) : operation.overpaidAmount,
            initialCredit,
            paymentStatus: isFullyPaid ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED
        })

        const savedOperation = await saveSaleOperation(table, paidOperation)

        // The overpaid amount is already in the customer credit in favor: the note only documents it
        if (overpaidAmount > 0) {
            await createOverpaymentNote(table, operation.id, overpaidAmount, paymentDate)
        }

        return savedOperation
    }

    const confirmSalePayment = async (
        table: TableName,
        operationId: string,
        paymentDate: Date,
        salePaymentItems: SalePaymentItemData[],
        amountToPay?: number
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = await loadSaleOperation(table, operationId, true)
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(loadedOperation!.customerId, true)

        return confirmPayment(table, loadedOperation!, loadedCustomer!, paymentDate, salePaymentItems, amountToPay)
    }

    const bulkConfirmSalePayments = async (
        table: TableName,
        operationIds: string[],
        paymentTypeId: string,
        cashFundId: string,
        paymentDate: Date
    ) => {
        const { loadSaleOperations } = saleOperationLoaders()
        const loadedOperations = await loadSaleOperations(operationIds, true)
        const sortedOperations = loadedOperations.filter(isPayable).sort((a, b) => a.code - b.code)
        
        const { loadCustomer } = customerLoaders()
        for (const operation of sortedOperations) {
            const customer = await loadCustomer(operation.customerId, true)
            const creditInFavor = Math.max(0, customer!.creditInFavor || 0)
            const amount = round(getAdjustedTotal(table, operation, paymentTypeId) - (operation.paidAmount || 0))
            const salePaymentItem: SalePaymentItemData = {
                index: '1',
                paymentTypeId,
                amount: amount - creditInFavor,
                cashFundId
            }
            await confirmPayment(table, operation, customer!, paymentDate, toList(salePaymentItem))
        }
    }

    const uploadPaymentReceipt = async (
        table: TableName,
        operationId: string,
        paymentItem: SalePaymentItem,
        receiptFile: File
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = await loadSaleOperation(table, operationId, true)
        const loadedPaymentItems = loadedOperation!.paymentItems

        const paymentItemMatches = (paymentItem_1: SalePaymentItem, paymentItem_2: SalePaymentItem) =>
            paymentItem_1.paymentTypeId === paymentItem_2.paymentTypeId && paymentItem_1.destinationId === paymentItem_2.destinationId && paymentItem_1.amount === paymentItem_2.amount

        const index = loadedPaymentItems.filter(paymentItem => paymentItem.receiptUrl).length + 1
        const ext = fileHelpers.getFileExtension(receiptFile)
        const filePath = paymentReceiptFilePath(table, loadedOperation!, index, ext)
        const receiptUrl = await saveFile(filePath, receiptFile)

        let matched = false
        const paymentItems = loadedPaymentItems.map(loadedPaymentItem => {
            if (!matched && paymentItemMatches(loadedPaymentItem, paymentItem)) {
                matched = true
                return buildSalePaymentItem({
                    ...loadedPaymentItem,
                    receiptUrl
                })
            }
            return loadedPaymentItem
        })

        const operation = buildSaleOperation(table, {
            ...loadedOperation,
            paymentItems
        })

        return saveSaleOperation(table, operation)
    }

    const invoiceSaleOperation = async (params: {
        fiscalDocumentId: string
        mode?: InvoiceMode
        itemQuantities?: InvoiceItemQuantity[]
        fullInvoice?: boolean
    }) => {
        const { fiscalDocumentId, mode, itemQuantities, fullInvoice } = params
        return emitFiscalDocument(fiscalDocumentId, { mode, itemQuantities, fullInvoice })
    }

    const archiveSaleOperation = async (
        table: TableName,
        operationId: string
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const operation = await loadSaleOperation(table, operationId, true)
        const archivedOperation = buildSaleOperation(table, {
            ...operation,
            archived: true
        })
        return saveSaleOperation(table, archivedOperation)
    }

    return {
        buildSaleOperationItems,
        buildEditedSaleOperationItems,
        updateReservedStock,
        applyAdjustmentNote,
        createManualCreditNote,
        updateSaleOperationProps,
        confirmSalePayment,
        uploadPaymentReceipt,
        bulkConfirmSalePayments,
        invoiceSaleOperation,
        archiveSaleOperation
    }
}
